"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const faqs = [{
  question: "How does data-driven revenue management work?",
  answer: "Data-driven revenue management works by leveraging real-time market intelligence and historical performance data to optimize your pricing strategy. By analyzing competitor rates, demand patterns, and booking velocity, we ensure your hotel captures maximum revenue during peak periods and maintains occupancy during low seasons."
}, {
  question: "Can you help with OTA listing optimization?",
  answer: "OTA listing optimization is achieved through high-converting content and professional photography recommendations. We specialize in enhancing your presence on platforms like Booking.com and Expedia, ensuring strategic rate parity management so your property stands out to potential guests."
}, {
  question: "What is included in a revenue audit?",
  answer: "A revenue audit provides a comprehensive evaluation of your current tech stack, pricing strategies, distribution channels, and market positioning. We deliver a detailed report identifying missed opportunities and actionable steps to improve your RevPAR and overall profitability."
}, {
  question: "Do you work with independent hotels?",
  answer: "We partner with independent hotels by providing bespoke, enterprise-level revenue strategies tailored to boutique properties and small chains. Our approach ensures you receive the same level of sophisticated expertise typically reserved for large international hotel brands."
}, {
  question: "How long does it take to see results?",
  answer: "Results typically manifest within 30 to 60 days of implementing our recommended strategies and tech stack adjustments. While every property is unique, our clients consistently see improvements in booking velocity and rate optimization within this timeframe."
}, {
  question: "What makes HotelRevBaBa different?",
  answer: "HotelRevBaBa differentiates itself through a hands-on, personalized consultancy approach led by Sunil Singh. We act as an extension of your team, solving complex revenue challenges with data-backed, human-centric strategies rather than just providing software."
}, {
  question: "Do you offer remote revenue management?",
  answer: "Remote revenue management allows you to access top-tier expertise without the overhead of a full-time, on-site manager. We provide flexible, remote services that integrate seamlessly with your existing operations to drive consistent growth."
}, {
  question: "How do I get started with a consultation?",
  answer: "Getting started is simple; book a complimentary discovery call through our website. During this session, we will discuss your property's specific goals and determine how our revenue management strategies can best support your growth."
}];
export default function Consultingfaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2463" id="consultingfaq" className="bg-slate-50 text-slate-900 py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      })
    }} />
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="lg:sticky lg:top-24">
              <div className="font-default text-xs tracking-widest uppercase text-blue-600 mb-8">
                COMMON INQUIRIES / 03
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mb-6">Revenue Management Consulting FAQs</h2>
              <p className="text-slate-600 mb-8">
                We provide clear, actionable answers to the most common questions from hotel owners and managers. If you have a specific challenge, we are ready to discuss it.
              </p>

              <h2 className="font-default font-bold text-slate-900 mb-10" style={{
              fontSize: "clamp(2.75rem, 5vw, 5rem)",
              lineHeight: "0.95",
              letterSpacing: "-0.03em"
            }}>
                Revenue
                <br />
                Growth
                <br />
                Insights.
              </h2>

              <p className="font-default text-base md:text-lg leading-relaxed mb-10" style={{
              maxWidth: "360px",
              color: "#475569"
            }}>
                We answer the most common questions from hotel owners and managers. If you have a specific challenge, we are ready to discuss it.
              </p>

              <Button asChild variant="outline" size="lg" className="font-default font-medium tracking-wider uppercase text-xs border-slate-900 text-slate-900 bg-transparent hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-300 px-8 py-6 h-auto">
                <Link to="/ContactPage">Book a Consultation</Link>
              </Button>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible defaultValue="item-0" className="flex flex-col gap-3">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-0 backdrop-blur-sm transition-all duration-300 group" style={{
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)"
            }}>
                  <AccordionTrigger className="hover:no-underline px-6 md:px-8 py-6 md:py-7 min-h-[96px] gap-6 [&>svg]:hidden group-hover:[border-color:rgba(0,0,0,0.1)]">
                    <div className="flex items-center gap-6 md:gap-8 flex-1 text-left">
                      <span className="font-default font-bold text-xs text-blue-600 shrink-0 tracking-wider">
                        Q.{String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-default font-medium text-base md:text-lg text-slate-900 leading-snug">
                        {faq.question}
                      </span>
                    </div>
                    <Plus className="size-6 text-blue-600 shrink-0 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                  </AccordionTrigger>
                  <AccordionContent className="px-6 md:px-8 pb-8 pt-0">
                    <div className="pl-0 md:pl-[88px]">
                      <p className="font-default text-base md:text-[1.0625rem]" style={{
                    lineHeight: "1.75",
                    maxWidth: "600px",
                    color: "#475569"
                  }}>
                        {faq.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            <div className="mt-16 p-8 bg-slate-900 text-white rounded-lg text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to implement?</h3>
              <p className="mb-8 text-slate-300">Let's discuss how we can optimize your hotel's revenue performance.</p>
              <Button asChild variant="secondary" size="lg">
                <Link to="/ContactPage">Book a Consultation</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}